import React from "react";
import { useMerchantAuthUser } from "../auth/MerchantAuthContext";
import { MerchantPlusIcon } from "../components/MerchantIcons";
import {
  fetchMerchantOutletGmvDistribution,
  fetchMerchantOutlets,
  getMerchantApiErrorMessage,
} from "../data/merchantApi";
import {
  formatMerchantCurrency,
  formatMerchantCompactCurrency,
} from "../data/merchantData";
import {
  pickMerchantField,
  toMerchantNumber,
  toMerchantString,
} from "../data/merchantLegacyUtils";

const merchantOutletsPageDataCache = new Map();

function formatOutletTooltipMonth(monthValue) {
  const normalizedMonth = toMerchantString(monthValue);
  const matched = normalizedMonth.match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return normalizedMonth || "Recent Month";
  }

  const monthDate = new Date(Date.UTC(Number(matched[1]), Number(matched[2]) - 1, 1));

  return monthDate.toLocaleString("en-SG", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function getCurrentOutletMonth() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return String(now.getFullYear()) + "-" + month;
}

function formatOutletMonthLabel(monthValue) {
  const normalizedMonth = toMerchantString(monthValue);
  const matched = normalizedMonth.match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return normalizedMonth || "Current Month";
  }

  const monthDate = new Date(Date.UTC(Number(matched[1]), Number(matched[2]) - 1, 1));

  return monthDate.toLocaleString("en-SG", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function normalizeOutletItem(item) {
  return {
    storeId: toMerchantString(pickMerchantField(item, ["store_id", "id"]), ""),
    name: toMerchantString(
      pickMerchantField(item, ["store_name", "name"]),
      "Unknown Outlet",
    ),
    location: toMerchantString(
      pickMerchantField(item, ["address", "location"]),
      "Merchant store",
    ),
    status: toMerchantString(pickMerchantField(item, ["status"]), "").toUpperCase(),
    gmv: toMerchantNumber(pickMerchantField(item, ["gmv"], 0)),
    transactions: toMerchantNumber(
      pickMerchantField(item, ["transaction_count", "transactions"], 0),
    ),
  };
}

function normalizeOutletsResponse(response) {
  return {
    period: toMerchantString(pickMerchantField(response, ["period"]), getCurrentOutletMonth()),
    activeOutletCount: toMerchantNumber(
      pickMerchantField(response, ["active_outlet_count"], 0),
    ),
    totalCount: toMerchantNumber(pickMerchantField(response, ["total_count"], 0)),
    items: Array.isArray(response && response.items)
      ? response.items.map(normalizeOutletItem)
      : [],
  };
}

function buildOutletBars(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const values = items.map(function mapItem(item) {
    return toMerchantNumber(pickMerchantField(item, ["gmv"], 0));
  });
  const minValue = Math.min.apply(null, values);
  const maxValue = Math.max.apply(null, values);
  const valueSpan = maxValue - minValue || 1;

  return items.map(function mapItem(item, index) {
    const value = values[index];

    if (maxValue === minValue) {
      return {
        month: toMerchantString(pickMerchantField(item, ["month"]), ""),
        gmv: value,
        height: 72,
      };
    }

    return {
      month: toMerchantString(pickMerchantField(item, ["month"]), ""),
      gmv: value,
      height: Math.round(40 + ((value - minValue) / valueSpan) * 60),
    };
  });
}

function applyDistributionSeriesToBars(outletBarsByStoreId, distributionItems) {
  distributionItems.forEach(function applyDistributionSeries(series) {
    const storeId = toMerchantString(pickMerchantField(series, ["store_id", "id"]), "");
    const items = Array.isArray(series && series.items) ? series.items : [];

    if (storeId) {
      outletBarsByStoreId[storeId] = buildOutletBars(items);
    }
  });

  return outletBarsByStoreId;
}

async function fetchMerchantOutletsPageData(month) {
  const cacheKey = month || getCurrentOutletMonth();
  const cachedEntry = merchantOutletsPageDataCache.get(cacheKey);

  if (cachedEntry && cachedEntry.data) {
    return cachedEntry.data;
  }

  if (cachedEntry && cachedEntry.promise) {
    return cachedEntry.promise;
  }

  const requestPromise = (async function requestPageData() {
    const outletsResponse = await fetchMerchantOutlets({ month: cacheKey });
    const normalizedResponse = normalizeOutletsResponse(outletsResponse);
    const nextPageData = {
      period: normalizedResponse.period,
      activeOutletCount: normalizedResponse.activeOutletCount,
      items: normalizedResponse.items,
      outletBarsByStoreId: {},
      feedbackMessage: "",
    };

    try {
      const distributionResponse = await fetchMerchantOutletGmvDistribution({ months: 12 });
      const distributionSeries = Array.isArray(distributionResponse && distributionResponse.items)
        ? distributionResponse.items
        : [];

      nextPageData.outletBarsByStoreId = applyDistributionSeriesToBars(
        nextPageData.outletBarsByStoreId,
        distributionSeries,
      );
    } catch (error) {
      nextPageData.feedbackMessage = getMerchantApiErrorMessage(
        error,
        "Outlet GMV distributions could not be loaded.",
      );
    }

    merchantOutletsPageDataCache.set(cacheKey, { data: nextPageData });

    return nextPageData;
  })().catch(function handleRequestError(error) {
    merchantOutletsPageDataCache.delete(cacheKey);
    throw error;
  });

  merchantOutletsPageDataCache.set(cacheKey, { promise: requestPromise });

  return requestPromise;
}

function formatOutletDisplayName(name) {
  return toMerchantString(name).replace("@", " · ");
}

export default function MerchantOutletsPage() {
  const authUser = useMerchantAuthUser();
  const [requestState, setRequestState] = React.useState("loading");
  const [feedbackMessage, setFeedbackMessage] = React.useState("");
  const [outletsPeriod, setOutletsPeriod] = React.useState(getCurrentOutletMonth());
  const [activeOutletCount, setActiveOutletCount] = React.useState(0);
  const [outlets, setOutlets] = React.useState([]);
  const [outletBarsByStoreId, setOutletBarsByStoreId] = React.useState({});
  const [hoveredBar, setHoveredBar] = React.useState(null);

  const merchantBusinessName = React.useMemo(
    function getMerchantBusinessName() {
      return toMerchantString(
        pickMerchantField(authUser, ["businessName", "name"]),
        "Merchant Account",
      );
    },
    [authUser],
  );

  React.useEffect(function loadOutletsPageData() {
    let isActive = true;
    const currentMonth = getCurrentOutletMonth();

    async function requestOutletsPageData() {
      setRequestState("loading");
      setFeedbackMessage("");

      if (!isActive) {
        return;
      }

      try {
        const pageData = await fetchMerchantOutletsPageData(currentMonth);

        if (!isActive) {
          return;
        }

        setOutletsPeriod(pageData.period);
        setActiveOutletCount(pageData.activeOutletCount);
        setOutlets(pageData.items);
        setOutletBarsByStoreId(pageData.outletBarsByStoreId);
        setFeedbackMessage(pageData.feedbackMessage);
        setRequestState("success");
      } catch (error) {
        if (!isActive) {
          return;
        }

        setOutletsPeriod(currentMonth);
        setActiveOutletCount(0);
        setOutlets([]);
        setOutletBarsByStoreId({});
        setRequestState("error");
        setFeedbackMessage(
          getMerchantApiErrorMessage(
            error,
            "Outlets list could not be loaded from the merchant API.",
          ),
        );
        return;
      }
    }

    requestOutletsPageData();

    return function cleanupOutletsPageData() {
      isActive = false;
    };
  }, []);

  return (
    <div className="merchant-page">
      <div className="merchant-page-header merchant-page-header-row">
        <div>
          <div className="merchant-page-title">Outlets</div>
          <div className="merchant-page-subtitle">
            {activeOutletCount.toLocaleString("en-SG")} active outlets · {merchantBusinessName}
          </div>
        </div>
        <button
          className="merchant-button merchant-button-primary"
          type="button"
          title="Outlet management is view-only for now"
          aria-disabled="true"
          onClick={function noop() {}}
        >
          <MerchantPlusIcon />
          Add Outlet
        </button>
      </div>

      {requestState === "loading" || feedbackMessage ? (
        <div className="merchant-data-note">
          {requestState === "loading"
            ? "Loading live outlets and GMV distribution from the merchant APIs."
            : feedbackMessage}
          {requestState === "loading" ? (
            <div className="merchant-inline-status merchant-inline-status-loading">
              <span className="merchant-request-spinner" />
              Request in progress
            </div>
          ) : null}
          {requestState === "success" && feedbackMessage ? (
            <div className="merchant-inline-status">{feedbackMessage}</div>
          ) : null}
        </div>
      ) : null}

      {outlets.length ? (
        <div className="merchant-outlet-grid">
          {outlets.map(function renderOutlet(outlet) {
            const bars = outletBarsByStoreId[outlet.storeId] || [];

            return (
              <div className="merchant-outlet-card" key={outlet.storeId || outlet.name}>
                <div className="merchant-outlet-card-title">
                  {formatOutletDisplayName(outlet.name)}
                </div>
                <div className="merchant-outlet-card-subtitle">
                  {outlet.location || "Merchant store"}
                </div>
                <div className="merchant-outlet-metrics">
                  <div>
                    <div className="merchant-outlet-metric-label">GMV</div>
                    <div className="merchant-outlet-metric-value">
                      {formatMerchantCompactCurrency(outlet.gmv)}
                    </div>
                  </div>
                  <div>
                    <div className="merchant-outlet-metric-label">
                      <span className="merchant-desktop-inline">Transactions</span>
                      <span className="merchant-mobile-inline">Txns</span>
                    </div>
                    <div className="merchant-outlet-metric-value">
                      {outlet.transactions.toLocaleString("en-SG")}
                    </div>
                  </div>
                </div>
                {bars.length ? (
                  <div className="merchant-mini-bars" aria-label="Recent GMV distribution">
                    {bars.map(function renderBar(bar, index) {
                      const isHovered =
                        hoveredBar &&
                        hoveredBar.storeId === outlet.storeId &&
                        hoveredBar.index === index;

                      return (
                        <span
                          key={(outlet.storeId || outlet.name) + "-bar-" + index}
                          className={isHovered ? "merchant-mini-bar is-hovered" : "merchant-mini-bar"}
                          style={{ height: bar.height + "%" }}
                          onMouseEnter={function handleBarMouseEnter() {
                            setHoveredBar({
                              storeId: outlet.storeId,
                              index: index,
                              monthLabel: formatOutletTooltipMonth(bar.month),
                              valueLabel: formatMerchantCurrency(bar.gmv, 2),
                            });
                          }}
                          onMouseLeave={function handleBarMouseLeave() {
                            setHoveredBar(function clearHoveredBar(currentValue) {
                              if (
                                currentValue &&
                                currentValue.storeId === outlet.storeId &&
                                currentValue.index === index
                              ) {
                                return null;
                              }

                              return currentValue;
                            });
                          }}
                        >
                          {isHovered ? (
                            <span className="merchant-outlet-bar-tooltip">
                              <span className="merchant-outlet-bar-tooltip-month">
                                {hoveredBar.monthLabel}
                              </span>
                              <span className="merchant-outlet-bar-tooltip-value">
                                {hoveredBar.valueLabel}
                              </span>
                            </span>
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="merchant-outlet-card merchant-card-empty">
          {requestState === "loading"
            ? "Loading outlets..."
            : "No outlets available for " + formatOutletMonthLabel(outletsPeriod) + "."}
        </div>
      )}
    </div>
  );
}
